import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  Grid,
  List,
  ListItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MemoryIcon from "@mui/icons-material/Memory";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BarChartIcon from "@mui/icons-material/BarChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React from "react";
import { Auth } from "aws-amplify";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCustomers } from "../dataaccess/fetchLLMCustomers";
import { fetchLLMUsageTiers } from "../dataaccess/fetchLLMUsageTiers";
import { useNavigate, useSearchParams } from "react-router-dom";
import MemoryAndKnowledgeGraph from "./MemoryAndKnowledgeGraph";
import { CustomerBillingView } from "./CustomerBillingView";
import { CustomerUsageLimitsView } from "./CustomerUsageLimitsView";

const Customers: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get("customerId");

  const [nameFilter, setNameFilter] = React.useState<string | undefined>(
    undefined
  );
  const [selectedCustomer, setSelectedCustomer] = React.useState<any | null>(
    null
  );
  const [viewMode, setViewMode] = React.useState<'usage' | 'memory' | 'billing'>('usage');
  const [expandedTiers, setExpandedTiers] = React.useState<Record<string, boolean>>({});
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);

  const {
    data,
    isLoading,
    isError,
    error: customersError,
  } = useQuery({
    queryKey: ["customers"],
    queryFn: () => fetchLLMCustomers(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const {
    data: usageTiersData,
    error: usageTiersError,
  } = useQuery({
    queryKey: ["usageTiers"],
    queryFn: () => fetchLLMUsageTiers(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  React.useEffect(() => {
    if (customersError) {
      console.error("Customers query failed:", customersError);
      const detailedCustomersError = customersError as any;
      console.error(
        "Customers query failed details:",
        JSON.stringify(
          {
            errors: detailedCustomersError?.errors,
            data: detailedCustomersError?.data,
            message: detailedCustomersError?.message,
          },
          null,
          2
        )
      );
    }
  }, [customersError]);

  React.useEffect(() => {
    if (usageTiersError) {
      console.error("Usage tiers query failed:", usageTiersError);
    }
  }, [usageTiersError]);

  // Auto-select customer from URL parameter
  React.useEffect(() => {
    if (
      customerIdFromUrl &&
      data?.data?.customersByProjectIdAndLastCallMadeAt?.items
    ) {
      const customer = data.data.customersByProjectIdAndLastCallMadeAt.items.find(
        (c: any) => c.id === customerIdFromUrl
      );
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [customerIdFromUrl, data]);

  const customers = React.useMemo(
    () => data?.data?.customersByProjectIdAndLastCallMadeAt?.items ?? [],
    [data]
  );

  const usageTiers = React.useMemo(
    () => usageTiersData?.data?.usageTiersByProjectId?.items ?? [],
    [usageTiersData]
  );

  // Check if user is admin
  const checkAdminAuth = async () => {
    try {
      const session = await Auth.currentSession();
      const groups = session.getAccessToken().payload["cognito:groups"] || [];
      setIsAdmin(groups.includes("llmasaservice-admin"));
    } catch (error) {
      console.error("Auth error:", error);
      setIsAdmin(false);
    }
  };

  // Check admin status on component mount
  React.useEffect(() => {
    checkAdminAuth();
  }, []);

  // Filter customers by name
  const filteredCustomers = React.useMemo(() => {
    return customers.filter((customer) => {
      if (
        nameFilter &&
        customer?.name
          ?.toLowerCase()
          .indexOf(nameFilter.toLowerCase()) === -1
      ) {
        return false;
      }
      return true;
    });
  }, [customers, nameFilter]);

  // Group customers by usage tier
  const customersByTier = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    
    // Initialize with all usage tiers
    usageTiers.forEach((tier) => {
      if (tier?.id) {
        grouped[tier.id] = [];
      }
    });
    
    // Add "No Usage Tier" group for customers without a tier
    grouped['no-tier'] = [];

    // Group filtered customers by their usage tier
    filteredCustomers.forEach((customer) => {
      const tierId = customer?.usageTierId || 'no-tier';
      if (!grouped[tierId]) {
        grouped[tierId] = [];
      }
      grouped[tierId].push(customer);
    });

    return grouped;
  }, [filteredCustomers, usageTiers]);

  // Also filter which tiers to show based on search
  const visibleTiers = React.useMemo(() => {
    if (!nameFilter) return usageTiers;
    
    return usageTiers.filter((tier) => {
      // Show tier if its name matches the filter
      if (tier?.name?.toLowerCase().includes(nameFilter.toLowerCase())) {
        return true;
      }
      // Or if it has customers that match the filter
      const tierId = tier?.id || '';
      return customersByTier[tierId]?.length > 0;
    });
  }, [usageTiers, nameFilter, customersByTier]);

  // Initialize expanded state for all tiers
  React.useEffect(() => {
    const initialExpanded: Record<string, boolean> = {};
    usageTiers.forEach((tier) => {
      if (tier?.id) {
        initialExpanded[tier.id] = true; // Start with all expanded
      }
    });
    initialExpanded['no-tier'] = true;
    setExpandedTiers(initialExpanded);
  }, [usageTiers]);

  const toggleTierExpansion = (tierId: string) => {
    setExpandedTiers(prev => ({
      ...prev,
      [tierId]: !prev[tierId]
    }));
  };

  const renderCustomerRow = (customer: any) => {
    const statusTone =
      customer?.status === "active" ? "primary" : "secondary";
    const callCount = customer?.numberOfCallsMade ?? 0;

    return (
      <ListItem
        key={customer?.id}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          borderLeft: `3px solid ${
            statusTone === "primary"
              ? theme.palette.primary.main
              : theme.palette.secondary.main
          }`,
          bgcolor:
            selectedCustomer?.id === customer?.id
              ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1)
              : "transparent",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.1 : 0.06),
          },
          pr: 1,
          pl: 2.5,
          py: 1,
          gap: 1.5,
        })}
        onClick={() => setSelectedCustomer(customer)}
      >
        <Box
          sx={(theme) => {
            const color =
              statusTone === "primary"
                ? theme.palette.primary.main
                : theme.palette.secondary.main;
            return {
              width: 76,
              minWidth: 76,
              flexShrink: 0,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              border: `1px solid ${alpha(color, 0.42)}`,
              bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.16 : 0.1),
              color:
                statusTone === "primary"
                  ? theme.palette.primary.main
                  : theme.palette.mode === "dark"
                  ? theme.palette.secondary.light
                  : theme.palette.secondary.dark,
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1,
              textAlign: "center",
            };
          }}
        >
          <Typography
            component="span"
            sx={{
              display: "block",
              fontSize: 15,
              fontWeight: 800,
              lineHeight: 1.05,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
            }}
          >
            {callCount}
          </Typography>
          <Typography
            component="span"
            sx={{
              display: "block",
              fontSize: 10,
              lineHeight: 1.1,
              mt: 0.25,
              textTransform: "uppercase",
            }}
          >
            calls
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          <Typography
            variant="body1"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: selectedCustomer?.id === customer?.id ? 700 : 500,
            }}
          >
            {customer?.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            status: {customer?.status}, last call made:{" "}
            {customer?.lastCallMadeAt
              ? new Date(customer?.lastCallMadeAt).toLocaleString()
              : "no calls yet"}
            , last replenished:{" "}
            {customer?.lastReplenishedAt
              ? new Date(customer?.lastReplenishedAt).toLocaleString()
              : "never"}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, ml: 1, display: "flex", gap: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`edit/${customer?.id}`);
            }}
          >
            Edit
          </Button>
        </Box>
      </ListItem>
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    console.error("Customers page rendering error state:", {
      customersError,
      usageTiersError,
      projectId: project?.id,
    });
    return <div>Error...</div>;
  }

  return (
    <>
      <Box p={2}>
        <Grid container spacing={2} sx={{ height: "calc(100vh - 100px)" }}>
          {/* Left Side - Customer List */}
          <Grid
            item
            xs={6}
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Enhanced Filter Panel */}
            <Paper
              elevation={3}
              style={{
                borderRadius: 3,
                height: 120,
                border: "1px solid #444",
                marginBottom: 16,
              }}
            >
              <Box sx={{ p: 2, height: "100%" }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FilterListIcon fontSize="small" />
                  Customer Filters
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={8}>
                    <TextField
                      variant="outlined"
                      onChange={(e) => setNameFilter(e.currentTarget.value)}
                      value={nameFilter}
                      size="small"
                      fullWidth
                      placeholder="Search customers or usage tiers"
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => {
                        setNameFilter("");
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Enhanced Customer List */}
            <Paper
              elevation={3}
              style={{ borderRadius: 3, border: "1px solid #444", flex: 1 }}
            >
              <Box height="100%" sx={{ overflow: "auto" }}>
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6">
                    Customers ({filteredCustomers.length})
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const allExpanded: Record<string, boolean> = { ...expandedTiers };
                        visibleTiers.forEach((tier) => {
                          if (tier?.id) {
                            allExpanded[tier.id] = true;
                          }
                        });
                        if (customersByTier['no-tier']?.length > 0) {
                          allExpanded['no-tier'] = true;
                        }
                        setExpandedTiers(allExpanded);
                      }}
                    >
                      Expand All
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const allCollapsed: Record<string, boolean> = { ...expandedTiers };
                        visibleTiers.forEach((tier) => {
                          if (tier?.id) {
                            allCollapsed[tier.id] = false;
                          }
                        });
                        if (customersByTier['no-tier']?.length > 0) {
                          allCollapsed['no-tier'] = false;
                        }
                        setExpandedTiers(allCollapsed);
                      }}
                    >
                      Collapse All
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate("new")}
                    >
                      New Customer
                    </Button>
                  </Box>
                </Box>

                <List sx={{ p: 0 }}>
                  {/* Render usage tiers with their customers */}
                  {visibleTiers.filter(tier => tier?.id && customersByTier[tier.id]?.length > 0).map((tier) => {
                    if (!tier?.id) return null;
                    return (
                    <React.Fragment key={tier.id}>
                      {/* Usage Tier Header */}
                      <ListItem 
                        sx={(theme) => ({ 
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === "dark" ? 0.18 : 0.08
                          ),
                          color: 'text.primary',
                          cursor: 'pointer',
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                          '&:hover': {
                            bgcolor: alpha(
                              theme.palette.primary.main,
                              theme.palette.mode === "dark" ? 0.26 : 0.13
                            ),
                          },
                        })}
                        onClick={() => toggleTierExpansion(tier.id!)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                          {expandedTiers[tier.id] ? (
                            <ExpandLessIcon color="primary" />
                          ) : (
                            <ExpandMoreIcon color="primary" />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                              {tier.name} (v{tier.version || "1"}) ({customersByTier[tier.id]?.length || 0})
                            </Typography>
                            {tier.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {tier.description}
                              </Typography>
                            )}
                          </Box>
                          {tier.status && (
                            <Chip
                              label={tier.status}
                              size="small"
                              color={tier.status === "ACTIVE" ? "primary" : "default"}
                              variant="outlined"
                              sx={{ height: 24, fontWeight: 800 }}
                            />
                          )}
                        </Box>
                      </ListItem>
                      
                      {/* Customers in this tier */}
                      <Collapse in={expandedTiers[tier.id] || false}>
                        {customersByTier[tier.id]?.map((customer) =>
                          renderCustomerRow(customer)
                        )}
                      </Collapse>
                    </React.Fragment>
                  )})}

                  {/* Customers without usage tier */}
                  {customersByTier['no-tier']?.length > 0 && (
                    <React.Fragment>
                      <ListItem 
                        sx={(theme) => ({ 
                          bgcolor: alpha(
                            theme.palette.text.secondary,
                            theme.palette.mode === "dark" ? 0.16 : 0.08
                          ),
                          color: 'text.primary',
                          cursor: 'pointer',
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                          '&:hover': {
                            bgcolor: alpha(
                              theme.palette.text.secondary,
                              theme.palette.mode === "dark" ? 0.24 : 0.13
                            ),
                          },
                        })}
                        onClick={() => toggleTierExpansion('no-tier')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                          {expandedTiers['no-tier'] ? (
                            <ExpandLessIcon color="action" />
                          ) : (
                            <ExpandMoreIcon color="action" />
                          )}
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                            No Usage Tier ({customersByTier['no-tier']?.length || 0})
                          </Typography>
                        </Box>
                      </ListItem>
                      
                      <Collapse in={expandedTiers['no-tier'] || false}>
                        {customersByTier['no-tier']?.map((customer) =>
                          renderCustomerRow(customer)
                        )}
                      </Collapse>
                    </React.Fragment>
                  )}
                </List>
              </Box>
            </Paper>
          </Grid>

          {/* Right Side - Customer Detail View */}
          <Grid item xs={6} sx={{ height: "100%" }}>
            <Paper
              elevation={3}
              style={{
                borderRadius: 3,
                border: "1px solid #444",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* View Toggle Header */}
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <ButtonGroup variant="outlined" fullWidth>
                  <Button 
                    variant={viewMode === 'usage' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('usage')}
                    startIcon={<BarChartIcon />}
                  >
                    Usage & Limits
                  </Button>
                  <Button 
                    variant={viewMode === 'memory' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('memory')}
                    startIcon={<MemoryIcon />}
                  >
                    Memory & Knowledge
                  </Button>
                  <Button 
                    variant={viewMode === 'billing' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('billing')}
                    startIcon={<AccountBalanceIcon />}
                  >
                    Billing
                  </Button>
                </ButtonGroup>
              </Box>

              {/* View Content */}
              <Box sx={{ flex: 1, overflow: "auto" }}>
                {viewMode === 'usage' && (
                  <CustomerUsageLimitsView
                    selectedCustomer={selectedCustomer}
                    navigate={navigate}
                    isAdmin={isAdmin}
                  />
                )}
                {viewMode === 'memory' && (
                  <MemoryAndKnowledgeGraph
                    selectedCustomer={selectedCustomer}
                  />
                )}
                {viewMode === 'billing' && (
                  <CustomerBillingView
                    selectedCustomer={selectedCustomer}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Customers;
